import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Box, createFilterOptions, TextField, Typography } from '@mui/material';

import { CountryFlag } from '@frontend/features/common/components/CountryFlag';

import { countryOptions, type CountryOption } from '../destinations';

const filterOptions = createFilterOptions<CountryOption>({
  stringify: (option) => `${option.name} ${option.code}`,
});

interface Props {
  label: string;
  /** ISO 3166-1 alpha-2 ('' = sin elegir). */
  value?: string;
  onChange: (code: string) => void;
  required?: boolean;
  /** El proveedor señaló ESTE campo como faltante: se marca el control. */
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  width?: number;
  disabled?: boolean;
}

/**
 * País por ISO-2 con el nombre en el idioma del usuario. El VALOR es el código
 * (language-neutral) y solo el label se traduce (21.15).
 */
export function CountrySelect({
  label,
  value,
  onChange,
  required,
  error,
  helperText,
  fullWidth,
  width = 220,
  disabled,
}: Props) {
  const { i18n } = useTranslation();
  const options = useMemo(() => countryOptions(i18n.language), [i18n.language]);
  const selected = options.find((option) => option.code === value?.toUpperCase()) ?? null;

  return (
    <Autocomplete
      autoHighlight
      openOnFocus
      size="small"
      sx={fullWidth ? undefined : { width }}
      fullWidth={fullWidth}
      disabled={disabled}
      options={options}
      value={selected}
      onChange={(_event, option) => onChange(option?.code ?? '')}
      filterOptions={filterOptions}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, selected) => option.code === selected.code}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.code}>
          <CountryFlag countryCode={option.code} />
          <Typography component="span" flex={1}>
            {option.name}
          </Typography>
          <Typography component="span" variant="caption" color="text.secondary">
            {option.code}
          </Typography>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            startAdornment: selected ? (
              <>
                <CountryFlag countryCode={selected.code} />
                {params.InputProps.startAdornment}
              </>
            ) : (
              params.InputProps.startAdornment
            ),
          }}
        />
      )}
    />
  );
}
